import { toast, ToastContainer, ToastPosition, Theme } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export interface NotificationOptions {
  position?: ToastPosition;
  autoClose?: number;
  hideProgressBar?: boolean;
  closeOnClick?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
  progress?: number;
  theme?: Theme;
}

const defaultOptions: NotificationOptions = {
  position: 'top-right',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'light',
};

/**
 * Show success notification
 */
export const notifySuccess = (message: string, options: NotificationOptions = {}) => {
  return toast.success(message, { ...defaultOptions, ...options });
};

/**
 * Show error notification
 */
export const notifyError = (message: string, options: NotificationOptions = {}) => {
  return toast.error(message, { ...defaultOptions, ...options });
};

/**
 * Show warning notification
 */
export const notifyWarning = (message: string, options: NotificationOptions = {}) => {
  return toast.warning(message, { ...defaultOptions, ...options });
};

/**
 * Show info notification
 */
export const notifyInfo = (message: string, options: NotificationOptions = {}) => {
  return toast.info(message, { ...defaultOptions, ...options });
};

/**
 * Show default notification
 */
export const notify = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', options: NotificationOptions = {}) => {
  switch (type) {
    case 'success':
      return notifySuccess(message, options);
    case 'error':
      return notifyError(message, options);
    case 'warning':
      return notifyWarning(message, options);
    case 'info':
    default:
      return notifyInfo(message, options);
  }
};

/**
 * Dismiss all notifications
 */
export const dismissAll = () => {
  toast.dismiss();
};

/**
 * Dismiss a specific notification
 */
export const dismiss = (toastId: string | number) => {
  toast.dismiss(toastId);
};

/**
 * Update an existing notification
 */
export const updateNotification = (
  toastId: string | number,
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info',
  options: NotificationOptions = {}
) => {
  return toast.update(toastId, {
    render: message,
    type,
    ...defaultOptions,
    ...options,
  });
};

/**
 * Notification Provider Component
 * This should be placed at the root of your app
 */
export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <ToastContainer
        position={defaultOptions.position}
        autoClose={defaultOptions.autoClose}
        hideProgressBar={defaultOptions.hideProgressBar}
        newestOnTop={false}
        closeOnClick={defaultOptions.closeOnClick}
        rtl={false}
        pauseOnFocusLoss
        draggable={defaultOptions.draggable}
        pauseOnHover={defaultOptions.pauseOnHover}
        theme={defaultOptions.theme}
        limit={5} // Limit to 5 notifications at once
      />
      {children}
    </>
  );
};

export default {
  notify,
  notifySuccess,
  notifyError,
  notifyWarning,
  notifyInfo,
  dismissAll,
  dismiss,
  updateNotification,
  NotificationProvider,
};
